import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import ImageHero from 'components/Common/ImageHero'
import Image from 'components/Common/Image'
import TextButton from 'LuxKit/Button/TextButton'
import SolidLinkedinIcon from 'LuxKit/Icons/solid/SolidLinkedinIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'

import WhyWorkForUs from './WhyWorkForUs/WhyWorkForUs'
import OpenPositions from './OpenPositions'
import MissionAndValues from './MissionAndValues/WhyWorkForUs'
import LeadershipTeam from './LeadershipTeam/LeadershipTeam'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import { scrollToHash } from 'lib/window/scrollUtils'
import { useLocation } from 'react-router-dom'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const OFFICE_GALLERY_IMAGES: Array<App.Image> = [
  { id: 'careers-breakout' },
  { id: 'careers-kitchen' },
  { id: 'careers-lunch' },
  { id: 'careers-maldives' },
  { id: 'careers-reception' },
  { id: 'careers-atrium' },
  { id: 'careers-south' },
]

export const enum CAREERS_PAGE_ANCHORS {
  OPEN_POSITIONS = 'open-positions',
}

const BestPlaces = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  text-align: center;

  img {
    max-width: 100%;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    text-align: left;
    align-items: center;

    p {
      margin-top: 0;
      margin-left: ${rem(64)};
    }

    img {
      display: block;
      width: ${rem(374)};
      max-width: initial;
    }
  }
`

const LinkedInWrapper = styled.div`
  margin-top: ${rem(48)};
  margin-bottom: ${rem(96)};
  text-align: center;
  > a {
    width: 100%;
    max-width: 280px;
  }
`
const SpacedSubtitle = styled(BodyText)`
  margin-top: ${rem(32)};
`

function CareersPage() {
  const intl = useIntl()
  const location = useLocation()

  useEffect(() => {
    scrollToHash(location.hash, true)
  }, [location])

  const careersTitle = intl.formatMessage({
    id: '/icS3/adZL',
    defaultMessage: 'Careers at Luxury Escapes',
    description: 'Page title for the careers page',
  })
  const careersMetaDescription = intl.formatMessage({
    id: 'Qa1iGP4wnF',
    defaultMessage: 'Are you passionate about travel or technology and looking for an exciting new opportunity? Come and join Luxury Escapes, recognised as one of the \u2018Best Places to Work 2021 & 2023\u2019 by the Australian Financial Review.',
    description: 'Meta description for the careers page',
  })

  return <>
    <HeadData
      title={careersTitle}
      description={careersMetaDescription}
    />
    <ImageHero
      alt="Luxury Escapes Melbourne Office"
      id="careers-melbourne-office"
      brightness={-35}
    >
      <LayoutContainer>
        <Heading
          variant="heading2"
          align="center"
          colour="neutral-eight"
          as="h1"
        >
          {careersTitle}
        </Heading>
        <SpacedSubtitle
          variant="large"
          align="center"
          colour="neutral-eight"
          forwardedAs="p"
        >
          Are you passionate about travel or technology and looking for an exciting new opportunity?
          <br />
          Come and join Luxury Escapes, recognised as one of the 'Best Places to Work 2021 & 2023' by the Australian Financial Review.
        </SpacedSubtitle>
      </LayoutContainer>
    </ImageHero>
    <Pane>
      <PaneBody size="lg xl-y">
        <BestPlaces>
          <Image id="best-places-to-work" alt="Best Places to work" enhancedFormats={false} />
          <BodyText variant="large" as="p">
            {intl.formatMessage({
              id: 'PRgYKwEobc',
              defaultMessage: 'Our team is a collective of curious, forward-thinking people, driven to challenge the industry and ourselves to bring unforgettable travel experiences to our customer community.',
              description: 'Description of the team culture on the careers page',
            })}
          </BodyText>
        </BestPlaces>
      </PaneBody>
    </Pane>
    <Pane background="neutral-six">
      <LayoutContainer>
        <ImageGallery images={OFFICE_GALLERY_IMAGES} />
      </LayoutContainer>
    </Pane>
    <WhyWorkForUs />
    <Pane>
      <PaneBody size="lg xl-y">
        <LayoutContainer>
          <VerticalSpacer gap={32}>
            <Heading
              id={CAREERS_PAGE_ANCHORS.OPEN_POSITIONS}
              variant="heading3"
              align="center"
              as="h2"
            >
              {intl.formatMessage({
                id: 'w5TTmpD8Qy',
                defaultMessage: 'Current opportunities',
                description: 'Section heading for current job opportunities on the careers page',
              })}
            </Heading>
            <Heading variant="heading5" align="center" as="h3">
              {intl.formatMessage({
                id: 'JCT1DsqC++',
                defaultMessage: 'Open Positions',
                description: 'Sub-heading for open job positions on the careers page',
              })}
            </Heading>
          </VerticalSpacer>
          {!IS_SSR && <OpenPositions />}
        </LayoutContainer>
      </PaneBody>
    </Pane>
    <MissionAndValues />
    <LeadershipTeam />
    <Pane>
      <PaneBody size="lg xl-y">
        <LayoutContainer>
          <Heading
            variant="heading3"
            align="center"
            as="h2"
          >
            {intl.formatMessage({
              id: 'xpobO0z+ze',
              defaultMessage: 'Connect with us on LinkedIn',
              description: 'Section heading for LinkedIn connection on the careers page',
            })}
          </Heading>
          <LinkedInWrapper>
            <TextButton
              kind="primary"
              href="https://www.linkedin.com/company/luxury-escapes/"
              target="_blank"
              endIcon={<SolidLinkedinIcon />}
              size="large"
            >
              {intl.formatMessage({
                id: 'DcgvBURPDe',
                defaultMessage: 'Connect',
                description: 'Button text to connect on LinkedIn on the careers page',
              })}
            </TextButton>
          </LinkedInWrapper>
        </LayoutContainer>
      </PaneBody>
    </Pane>
  </>
}

export default CareersPage
